import React, { useRef, useState } from 'react';
import { HomeOutlined, SearchOutlined } from '@ant-design/icons';
import { Breadcrumb, Button, Input, Space, Table, Tag } from "antd";
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import "./request_part_sales.css";
import withRequestPartSalesLogic, { RequestPartSalesData } from '../../components/hocRequestPartSales';

interface DataType {
    key: React.Key;
    status: string;
    date_request: string;
    so_no: string;
    pr_no: string;
    sales: string;
    warehouse: string;
}

type DataIndex = keyof DataType;
interface RequestPartSalesProps {
    dataSource: RequestPartSalesData[];
    isLoading: boolean;
}

const RequestPartSales: React.FC<RequestPartSalesProps> = ({ dataSource, isLoading }) => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };


    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };


    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });


    const columns: TableColumnsType<DataType> = [
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            sorter: (a, b) => a.status.localeCompare(b.status),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('status'),
            render: (text: string) => {
                let statusLabel: string;
                let color: string;

                switch (text) {
                    case '1':
                        statusLabel = 'On Request';
                        color = 'orange';
                        break;
                    case '2':
                        statusLabel = 'Process On PO';
                        color = 'blue';
                        break;
                    case '3':
                        statusLabel = 'Success Picking';
                        color = 'green';
                        break;
                    case '4':
                        statusLabel = 'Picking Partial';
                        color = 'purple';
                        break;
                    case '5':
                        statusLabel = 'Ready To Picking';
                        color = 'red';
                        break;
                    default:
                        statusLabel = text ? text.toString() : '';
                        color = 'default';
                }

                const tagContent = searchedColumn === 'status' ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[searchText]}
                        autoEscape
                        textToHighlight={statusLabel}
                    />
                ) : (
                    statusLabel
                );

                return <Tag color={color} key={text}>{tagContent}</Tag>;
            },
        },
        {
            title: 'Date Request',
            dataIndex: 'date_request',
            key: 'date_request',
            sorter: (a, b) => a.date_request.localeCompare(b.date_request),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('date_request'),
        },
        {
            title: 'SO No',
            dataIndex: 'so_no',
            key: 'so_no',
            sorter: (a, b) => a.so_no.localeCompare(b.so_no),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('so_no'),
        },
        {
            title: 'PR No',
            dataIndex: 'pr_no',
            key: 'pr_no',
            sorter: (a, b) => a.pr_no.localeCompare(b.pr_no),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('pr_no'),
        },
        {
            title: 'Sales',
            dataIndex: 'sales',
            key: 'sales',
            sorter: (a, b) => a.sales.localeCompare(b.sales),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('sales'),
        },
        {
            title: 'Warehouse',
            dataIndex: 'warehouse',
            key: 'warehouse',
            sorter: (a, b) => a.warehouse.localeCompare(b.warehouse),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('warehouse'),
        },
    ];

    return (

        <div id="ilqt" style={transformStyle}>
            <div className="gjs-grid-row" id="ir4q">
                <div className="gjs-grid-column" id="ihv9">
                    <Breadcrumb
                        items={[
                            {
                                title: <HomeOutlined />
                            },
                            {
                                href: '#/dashboard/sales_browser',
                                title: (
                                    <>
                                        <span>Data Sales Master</span>
                                    </>
                                )
                            },
                            {
                                href: '#/dashboard/sales_estimation',
                                title: (
                                    <>
                                        <span>Sales Master</span>
                                    </>
                                )
                            },
                            {
                                title: "Request Part Sales",
                            },
                        ]}
                    />
                </div>
            </div>

            <div className="gjs-grid-row" id="ivnuk">
                <div className="gjs-grid-column" id="idx3p">
                    <Table<DataType> columns={columns} dataSource={dataSource} loading={isLoading} />
                </div>
            </div>
        </div>
    );
};

export default withRequestPartSalesLogic(RequestPartSales); 
